/*
 * Stan przypięcia menu bocznego (desktop) jako czysta logika, bez Reacta, żeby dało się ją
 * przetestować jednostkowo. `preference` to zapisany wybór użytkownika (localStorage). Strony mogą
 * dodatkowo prosić o chwilowe zwinięcie menu (licznik `collapseRequests`, useSidebarAutoCollapse);
 * taka prośba nie zmienia preferencji, po zwolnieniu ostatniej menu wraca do poprzedniego stanu.
 * Ręczne przypięcie w trakcie prośby (`pinnedDespiteRequest`) wygrywa, dopóki prośby trwają. Przy nawigacji
 * formularz -> formularz z otwartym czatem stary formularz zwalnia prośbę, a nowy zgłasza ją w tym samym
 * commicie, więc reset przypięcia robi dopiero `settleCollapse` z efektu Layoutu po commicie, gdy licznik
 * nadal jest 0 (np. po zamknięciu czatu). Przy liczniku 0 flaga i tak nie wpływa na wynik.
 */
export const SIDEBAR_PINNED_KEY = 'hs-sidebar-pinned';

export interface SidebarPinnedState {
  /** Zapisana preferencja użytkownika (pinezka w stopce menu). */
  preference: boolean;
  /** Liczba aktywnych próśb o zwinięcie; każda para wywołań hooka (true/false) to +1/-1. */
  collapseRequests: number;
  /** Użytkownik przypiął menu w trakcie prośby o zwinięcie: prośba przestaje działać do jej zwolnienia. */
  pinnedDespiteRequest: boolean;
}

export type SidebarPinnedAction =
  | { type: 'requestCollapse' }
  | { type: 'releaseCollapse' }
  | { type: 'settleCollapse' }
  | { type: 'togglePinned' };

export const createSidebarPinnedState = (preference: boolean): SidebarPinnedState => ({
  preference,
  collapseRequests: 0,
  pinnedDespiteRequest: false,
});

/** Menu jest chwilowo zwinięte na prośbę strony (i użytkownik nie przypiął go w międzyczasie). */
export const isSidebarAutoCollapsed = (state: SidebarPinnedState): boolean => (
  state.collapseRequests > 0 && !state.pinnedDespiteRequest
);

/** Czy menu ma być teraz rozwinięte na stałe (preferencja minus chwilowe zwinięcie). */
export const resolveSidebarPinned = (state: SidebarPinnedState): boolean => (
  state.preference && !isSidebarAutoCollapsed(state)
);

/**
 * Preferencja po kliknięciu pinezki. Menu zwinięte automatycznie wygląda na odpięte,
 * więc kliknięcie znaczy wtedy „przypnij teraz”, a nie „odepnij”.
 */
export const toggledSidebarPinnedPreference = (state: SidebarPinnedState): boolean => (
  isSidebarAutoCollapsed(state) ? true : !state.preference
);

export const sidebarPinnedReducer = (state: SidebarPinnedState, action: SidebarPinnedAction): SidebarPinnedState => {
  switch (action.type) {
    case 'requestCollapse':
      return { ...state, collapseRequests: state.collapseRequests + 1 };
    case 'releaseCollapse':
      return { ...state, collapseRequests: Math.max(0, state.collapseRequests - 1) };
    case 'settleCollapse':
      // Ten sam obiekt, gdy nie ma czego resetować: React pomija wtedy render.
      if (state.collapseRequests > 0 || !state.pinnedDespiteRequest) return state;
      return { ...state, pinnedDespiteRequest: false };
    case 'togglePinned':
      return {
        ...state,
        preference: toggledSidebarPinnedPreference(state),
        pinnedDespiteRequest: state.pinnedDespiteRequest || isSidebarAutoCollapsed(state),
      };
    default:
      return state;
  }
};

export const readSidebarPinnedPreference = (): boolean => {
  try {
    return window.localStorage.getItem(SIDEBAR_PINNED_KEY) === '1';
  } catch {
    return false;
  }
};

export const persistSidebarPinnedPreference = (next: boolean): void => {
  try {
    window.localStorage.setItem(SIDEBAR_PINNED_KEY, next ? '1' : '0');
  } catch {
    // brak dostępu do localStorage, stan zostaje tylko w pamięci
  }
};

/** Stan początkowy dla useReducer w Layoucie: preferencja z przeglądarki, bez aktywnych próśb. */
export const initSidebarPinnedState = (): SidebarPinnedState => createSidebarPinnedState(readSidebarPinnedPreference());
